import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Newspaper, Plus, Edit2, Trash2, Eye, EyeOff, Loader2, Calendar, Tag, X, ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from './ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD').replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);

const emptyForm = {
  id: null,
  title_fr: '',
  title_en: '',
  slug: '',
  summary_fr: '',
  summary_en: '',
  body_fr: '',
  body_en: '',
  hero_image_url: '',
  publish_date: new Date().toISOString().slice(0, 10),
  tagsText: '',  // comma-separated in the form
  is_published: true,
};

export const AdminPressReleasesPanel = () => {
  const { language } = useLanguage();
  const { token } = useAuth();
  const T = (fr, en) => (language === 'fr' ? fr : en);

  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null = closed; object = open
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/press/releases/admin/list`, authHeaders);
      setReleases(data || []);
    } catch (err) {
      toast.error(T('Impossible de charger les communiqués', 'Failed to load releases'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.resolve().then(() => reload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openCreate = () => {
    setEditing({ mode: 'create' });
    setForm(emptyForm);
    setSlugTouched(false);
  };

  const openEdit = (release) => {
    setEditing({ mode: 'edit', id: release.id });
    setForm({
      id: release.id,
      title_fr: release.title_fr || '',
      title_en: release.title_en || '',
      slug: release.slug || '',
      summary_fr: release.summary_fr || '',
      summary_en: release.summary_en || '',
      body_fr: release.body_fr || '',
      body_en: release.body_en || '',
      hero_image_url: release.hero_image_url || '',
      publish_date: (release.publish_date || '').slice(0, 10),
      tagsText: (release.tags || []).join(', '),
      is_published: release.is_published !== false,
    });
    setSlugTouched(true);  // don't auto-replace existing slug
  };

  const close = () => {
    if (submitting) return;
    setEditing(null);
    setForm(emptyForm);
    setSlugTouched(false);
  };

  const onTitleFrChange = (value) => {
    setForm((f) => ({
      ...f,
      title_fr: value,
      slug: !slugTouched ? slugify(value) : f.slug,
    }));
  };

  const onSlugChange = (value) => {
    setSlugTouched(true);
    setForm({ ...form, slug: slugify(value) });
  };

  const submit = async () => {
    // Validate
    if (!form.title_fr.trim() || !form.title_en.trim() || !form.slug || !form.body_fr.trim() || !form.body_en.trim()) {
      toast.error(T('Champs obligatoires manquants', 'Required fields missing'));
      return;
    }
    if (form.body_fr.length < 10 || form.body_en.length < 10) {
      toast.error(T('Le contenu doit faire au moins 10 caractères', 'Body must be at least 10 characters'));
      return;
    }
    const tags = form.tagsText.split(',').map((t) => t.trim()).filter(Boolean);
    const payload = {
      title_fr: form.title_fr.trim(),
      title_en: form.title_en.trim(),
      slug: form.slug,
      summary_fr: form.summary_fr.trim() || null,
      summary_en: form.summary_en.trim() || null,
      body_fr: form.body_fr,
      body_en: form.body_en,
      hero_image_url: form.hero_image_url.trim() || null,
      publish_date: form.publish_date || null,
      tags,
      is_published: !!form.is_published,
    };
    setSubmitting(true);
    try {
      if (editing?.mode === 'create') {
        await axios.post(`${API}/press/releases`, payload, authHeaders);
        toast.success(T('Communiqué créé', 'Release created'));
      } else {
        await axios.put(`${API}/press/releases/${editing.id}`, payload, authHeaders);
        toast.success(T('Communiqué mis à jour', 'Release updated'));
      }
      close();
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Échec de l\'enregistrement', 'Save failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const togglePublished = async (release) => {
    try {
      await axios.put(
        `${API}/press/releases/${release.id}`,
        { is_published: !release.is_published },
        authHeaders,
      );
      toast.success(T('Mise à jour effectuée', 'Updated'));
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Échec', 'Failed'));
    }
  };

  const deleteRelease = async (release) => {
    try {
      await axios.delete(`${API}/press/releases/${release.id}`, authHeaders);
      toast.success(T('Communiqué supprimé', 'Release deleted'));
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Échec de la suppression', 'Delete failed'));
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#0F4C5C]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-press-releases-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#0F4C5C] flex items-center gap-2">
            <Newspaper className="w-5 h-5" />
            {T('Communiqués de presse', 'Press releases')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {T(`${releases.length} communiqué(s) — ${releases.filter((r) => r.is_published).length} publié(s)`,
               `${releases.length} release(s) — ${releases.filter((r) => r.is_published).length} published`)}
          </p>
        </div>
        <Button onClick={openCreate} className="btn-primary" data-testid="admin-new-release-btn">
          <Plus className="w-4 h-4 mr-2" />
          {T('Nouveau communiqué', 'New release')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {releases.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              {T('Aucun communiqué pour le moment.', 'No releases yet.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{T('Titre', 'Title')}</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>{T('Date', 'Date')}</TableHead>
                    <TableHead>Tags</TableHead>
                    <TableHead>{T('Statut', 'Status')}</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {releases.map((r) => (
                    <TableRow key={r.id} data-testid={`admin-release-row-${r.slug}`}>
                      <TableCell>
                        <p className="font-semibold text-[#0F4C5C] line-clamp-1">{r.title_fr}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{r.title_en}</p>
                      </TableCell>
                      <TableCell><code className="text-xs">{r.slug}</code></TableCell>
                      <TableCell className="text-sm">{r.publish_date?.slice(0, 10) || '—'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(r.tags || []).slice(0, 3).map((t) => (
                            <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                          ))}
                          {(r.tags || []).length > 3 && (
                            <Badge variant="outline" className="text-xs">+{r.tags.length - 3}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.is_published ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                            <Eye className="w-3 h-3 mr-1" /> {T('Publié', 'Published')}
                          </Badge>
                        ) : (
                          <Badge variant="outline">
                            <EyeOff className="w-3 h-3 mr-1" /> {T('Brouillon', 'Draft')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => window.open(`/press/releases/${r.slug}`, '_blank')}
                            data-testid={`admin-release-preview-${r.slug}`}
                            title={T('Aperçu public', 'Public preview')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => togglePublished(r)}
                            data-testid={`admin-release-toggle-${r.slug}`}
                            title={r.is_published ? T('Dépublier', 'Unpublish') : T('Publier', 'Publish')}
                          >
                            {r.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openEdit(r)}
                            data-testid={`admin-release-edit-${r.slug}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700" data-testid={`admin-release-delete-${r.slug}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{T('Supprimer ce communiqué ?', 'Delete this release?')}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {T(`Le communiqué "${r.title_fr}" sera supprimé définitivement.`,
                                     `Release "${r.title_fr}" will be permanently deleted.`)}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{T('Annuler', 'Cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteRelease(r)}
                                  className="bg-rose-600 hover:bg-rose-700"
                                  data-testid={`admin-release-delete-confirm-${r.slug}`}
                                >
                                  {T('Supprimer', 'Delete')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit / Create dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) close(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-release-dialog">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Newspaper className="w-5 h-5 text-[#0F4C5C]" />
                  {editing.mode === 'create' ? T('Nouveau communiqué', 'New release') : T('Modifier le communiqué', 'Edit release')}
                </DialogTitle>
                <DialogDescription>
                  {T('Saisissez le contenu en français ET en anglais (les deux sont obligatoires).',
                     'Enter content in both French AND English (both required).')}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Titles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="rel-title-fr">{T('Titre (FR)', 'Title (FR)')} *</Label>
                    <Input
                      id="rel-title-fr"
                      data-testid="release-title-fr"
                      value={form.title_fr}
                      onChange={(e) => onTitleFrChange(e.target.value)}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rel-title-en">{T('Titre (EN)', 'Title (EN)')} *</Label>
                    <Input
                      id="rel-title-en"
                      data-testid="release-title-en"
                      value={form.title_en}
                      onChange={(e) => setForm({ ...form, title_en: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="rel-slug">Slug (URL) *</Label>
                  <Input
                    id="rel-slug"
                    data-testid="release-slug"
                    value={form.slug}
                    onChange={(e) => onSlugChange(e.target.value)}
                    placeholder="mon-nouveau-communique"
                    className="mt-1.5 font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    /press/releases/<strong>{form.slug || 'mon-slug'}</strong>
                  </p>
                </div>

                {/* Summaries */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="rel-summary-fr">{T('Résumé (FR)', 'Summary (FR)')}</Label>
                    <Textarea
                      id="rel-summary-fr"
                      data-testid="release-summary-fr"
                      rows={2}
                      maxLength={600}
                      value={form.summary_fr}
                      onChange={(e) => setForm({ ...form, summary_fr: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rel-summary-en">{T('Résumé (EN)', 'Summary (EN)')}</Label>
                    <Textarea
                      id="rel-summary-en"
                      data-testid="release-summary-en"
                      rows={2}
                      maxLength={600}
                      value={form.summary_en}
                      onChange={(e) => setForm({ ...form, summary_en: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                {/* Body */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="rel-body-fr">{T('Contenu (FR)', 'Body (FR)')} *</Label>
                    <Textarea
                      id="rel-body-fr"
                      data-testid="release-body-fr"
                      rows={8}
                      value={form.body_fr}
                      onChange={(e) => setForm({ ...form, body_fr: e.target.value })}
                      placeholder={T('Séparez vos paragraphes par une ligne vide.', 'Separate paragraphs with a blank line.')}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rel-body-en">{T('Contenu (EN)', 'Body (EN)')} *</Label>
                    <Textarea
                      id="rel-body-en"
                      data-testid="release-body-en"
                      rows={8}
                      value={form.body_en}
                      onChange={(e) => setForm({ ...form, body_en: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                {/* Hero image + date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="rel-hero">{T('Image hero (URL)', 'Hero image (URL)')}</Label>
                    <Input
                      id="rel-hero"
                      data-testid="release-hero-url"
                      type="url"
                      value={form.hero_image_url}
                      onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
                      placeholder="https://images.unsplash.com/..."
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rel-date" className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {T('Date de publication', 'Publish date')}
                    </Label>
                    <Input
                      id="rel-date"
                      data-testid="release-publish-date"
                      type="date"
                      value={form.publish_date}
                      onChange={(e) => setForm({ ...form, publish_date: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <Label htmlFor="rel-tags" className="flex items-center gap-1">
                    <Tag className="w-3 h-3" /> Tags
                  </Label>
                  <Input
                    id="rel-tags"
                    data-testid="release-tags"
                    value={form.tagsText}
                    onChange={(e) => setForm({ ...form, tagsText: e.target.value })}
                    placeholder={T('Séparés par des virgules, ex : levée, croissance, europe', 'Comma-separated, e.g.: funding, growth, europe')}
                    className="mt-1.5"
                  />
                </div>

                {/* Published toggle */}
                <div className="flex items-center gap-2 bg-[#FBF8F3] rounded-lg p-3 border border-[#0F4C5C]/10">
                  <input
                    type="checkbox"
                    id="rel-published"
                    data-testid="release-is-published"
                    checked={form.is_published}
                    onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <Label htmlFor="rel-published" className="cursor-pointer text-sm font-normal">
                    {T('Publié immédiatement (visible sur le site public)', 'Published immediately (visible on public site)')}
                  </Label>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={close} disabled={submitting}>
                  <X className="w-4 h-4 mr-1" />
                  {T('Annuler', 'Cancel')}
                </Button>
                <Button onClick={submit} disabled={submitting} className="btn-primary" data-testid="release-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Newspaper className="w-4 h-4 mr-2" />}
                  {editing.mode === 'create' ? T('Créer le communiqué', 'Create release') : T('Enregistrer', 'Save')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
